import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Routes, Route } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import { LanguageSelector } from './LanguageSelector';
import { EntryChoice } from './EntryChoice';
import { ExperienceSelector } from './ExperienceSelector';
import { CategoryView } from './CategoryView';
import { TopCartBar } from './TopCartBar';
import { SelectionSheet } from './SelectionSheet';
import { Badge } from './ui/badge';
import { useBackgroundOrderNotifications } from '../hooks/useOrderNotifications';
import { RefreshCw, WifiOff, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context to track current table
export const TableContext = React.createContext({
  tableNumber: null,
  setTableNumber: () => {}
});

export const useTable = () => React.useContext(TableContext);

const TableMenuWrapper = () => {
  const { tableNumber: urlTableNumber } = useParams();
  const navigate = useNavigate();
  const { language, clearLanguage } = useLanguage();
  const [tableNumber, setTableNumber] = useState(null);
  const [tableValid, setTableValid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState(null); // 'network' | 'not_found' | null
  const [retryCount, setRetryCount] = useState(0);
  // Entry choice: 'guided' | 'menu' | null. Persists in sessionStorage so back-nav works.
  const [entryChoice, setEntryChoice] = useState(() => sessionStorage.getItem('pont10_entry_choice'));

  // Fresh session = no entry_choice in sessionStorage → also clear stale language
  // so the customer always starts at the language picker on every new session.
  useEffect(() => {
    if (!sessionStorage.getItem('pont10_entry_choice')) {
      const stored = localStorage.getItem('pont10_language');
      if (stored && clearLanguage) clearLanguage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync entryChoice with sessionStorage whenever language clears (e.g. on PONT10 reset).
  // Without this, navigate(replace:true) keeps the cached state and skips EntryChoice
  // after the customer re-picks language.
  useEffect(() => {
    if (!language) {
      const fromStorage = sessionStorage.getItem('pont10_entry_choice');
      if (entryChoice !== fromStorage) setEntryChoice(fromStorage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const validateTable = useCallback(async (isRetry = false) => {
    // Free session mode (no table) — root /menu route, single-use anonymous session
    if (!urlTableNumber) {
      setTableNumber(null);
      setTableValid(true);
      setErrorType(null);
      sessionStorage.removeItem('mesa_numero');
      setLoading(false);
      return;
    }

    const num = parseInt(urlTableNumber);
    if (isNaN(num)) {
      setTableValid(false);
      setErrorType('not_found');
      setLoading(false);
      return;
    }

    if (isRetry) {
      setLoading(true);
      setErrorType(null);
    }

    // Try multiple times with increasing delays
    const maxRetries = 3;
    const delays = [0, 1000, 2000];

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, delays[attempt]));
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

        const response = await fetch(`${API_URL}/api/mesas/${num}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          // Check if table changed - if so, clear language to show selector
          const previousTable = sessionStorage.getItem('mesa_numero');
          if (previousTable && previousTable !== num.toString()) {
            // New table scanned - clear language and entry choice to force fresh start
            localStorage.removeItem('pont10_language');
            sessionStorage.removeItem('pont10_entry_choice');
            setEntryChoice(null);
          }
          
          setTableNumber(num);
          setTableValid(true);
          setErrorType(null);
          // Store in sessionStorage for the selection sheet
          sessionStorage.setItem('mesa_numero', num.toString());
          setLoading(false);
          return;
        } else if (response.status === 404) {
          setTableValid(false);
          setErrorType('not_found');
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error(`Table validation attempt ${attempt + 1} failed:`, error);
        // Continue to next retry
      }
    }

    // All retries failed
    setTableValid(false);
    setErrorType('network');
    setLoading(false);
  }, [urlTableNumber]);

  useEffect(() => {
    validateTable();
  }, [validateTable]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    validateTable(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#FDFCF8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-600 mb-4"></div>
        <p className="text-stone-500 text-sm">Conectando...</p>
      </div>
    );
  }

  // Network error - server not responding
  if (errorType === 'network') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ backgroundColor: '#FDFCF8' }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
            <WifiOff className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mb-3">Conexión perdida</h1>
          <p className="text-stone-500 mb-6 text-sm leading-relaxed">
            No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet e inténtalo de nuevo.
          </p>
          <button
            onClick={handleRetry}
            className="w-full px-6 py-3 bg-stone-800 text-white rounded-xl hover:bg-stone-700 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reintentar
          </button>
          <p className="text-stone-400 text-xs mt-4">
            Si el problema persiste, espera unos segundos y vuelve a intentarlo.
          </p>
        </div>
      </div>
    );
  }

  // Table not found
  if (tableValid === false) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ backgroundColor: '#FDFCF8' }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mb-3">Mesa no encontrada</h1>
          <p className="text-stone-500 mb-6 text-sm leading-relaxed">
            La mesa #{urlTableNumber} no existe o no está disponible en este momento.
          </p>
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full px-6 py-3 bg-stone-800 text-white rounded-xl hover:bg-stone-700 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reintentar
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 border border-stone-300 text-stone-700 rounded-xl hover:bg-stone-50 transition-colors"
            >
              Ir al menú principal
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no language selected, show language selector with table badge
  if (!language) {
    return (
      <TableContext.Provider value={{ tableNumber, setTableNumber }}>
        <div className="relative">
          {/* Table indicator (only if table linked) */}
          {tableNumber && (
            <div className="fixed top-4 right-4 z-50">
              <Badge variant="outline" className="border-stone-400 text-stone-600 text-sm px-3 py-1.5 bg-white/80 backdrop-blur-sm">
                #{tableNumber}
              </Badge>
            </div>
          )}
          <LanguageSelector />
        </div>
      </TableContext.Provider>
    );
  }

  // Language selected but customer hasn't chosen experience type yet → entry choice
  if (entryChoice !== 'menu') {
    return (
      <TableContext.Provider value={{ tableNumber, setTableNumber }}>
        <div className="relative">
          {tableNumber && (
            <div className="fixed top-4 right-4 z-50">
              <Badge variant="outline" className="border-stone-400 text-stone-300 text-sm px-3 py-1.5 bg-black/40 backdrop-blur-sm">
                #{tableNumber}
              </Badge>
            </div>
          )}
          <EntryChoice onChooseMenu={() => setEntryChoice('menu')} />
        </div>
      </TableContext.Provider>
    );
  }

  // Full menu experience with table context
  // Note: Table badge is now handled inside each component's header
  return (
    <TableContext.Provider value={{ tableNumber, setTableNumber }}>
      {/* Background notification monitor */}
      <BackgroundNotificationMonitor tableNumber={tableNumber} language={language} />
      
      {/* Persistent top cart bar */}
      <TopCartBar />
      
      {/* Content — no spacer, views handle TopCartBar offset internally */}
      <div>
        {/* Menu routes */}
        <Routes>
          <Route index element={<ExperienceSelector />} />
          <Route path="menu/:experienceId" element={<CategoryView />} />
          <Route path="menu/:experienceId/:categoryId" element={<CategoryView />} />
        </Routes>
      </div>
      
      {/* Selection sheet - will detect table number from sessionStorage */}
      <SelectionSheet />
    </TableContext.Provider>
  );
};

// Background notification monitor component
const BackgroundNotificationMonitor = ({ tableNumber, language }) => {
  useBackgroundOrderNotifications(tableNumber, language);
  return null;
};

export default TableMenuWrapper;
